
import { defineComponent, PropType, watch, nextTick } from 'vue';
import { CurrencyDisplay, useCurrencyInput } from 'vue-currency-input';
import { NUMBER_FORMAT_LOCALE } from '@/core/helpers/number-format';

export default defineComponent({
  name: 'CurrencyInputCustom',

  props: {
    classStr: {
      type: String,
    },
    currency: {
      type: String,
      default: 'EUR',
    },
    inputType: {
      type: String,
      default: 'text',
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    min: {
      type: Number as PropType<number | undefined>,
      default: undefined,
    },
    max: {
      type: Number as PropType<number>,
      default: Number.POSITIVE_INFINITY,
    },
    modelValue: Number,
    name: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    isReadonly: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['input', 'update:modelValue', 'change', 'blur', 'focus'],

  setup(props, { emit }) {
    const { inputRef, formattedValue, numberValue, setValue } = useCurrencyInput({
      autoDecimalDigits: false,
      currency: props.currency,
      currencyDisplay: CurrencyDisplay.hidden,
      locale: NUMBER_FORMAT_LOCALE,
      precision: 0,
      useGrouping: true,
      hideGroupingSeparatorOnFocus: false,
      hideCurrencySymbolOnFocus: true,
      hideNegligibleDecimalDigitsOnFocus: true,
      valueRange: {
        ...(props.min !== undefined ? { min: props.min } : {}),
        max: props.max,
      },
    });

    watch(
      () => props.modelValue,
      (value) => {
        if (value !== numberValue.value) {
          setValue(value ?? null);
        }
      },
      { immediate: true }
    );

    watch(numberValue, (value) => {
      if (value !== props.modelValue) {
        emit('update:modelValue', value);
      }
    });

    const getNumericValue = () => numberValue.value;

    const handleInput = () => {
      const numericValue = getNumericValue();
      emit('input', { value: numericValue });
    };

    const handleChange = () => {
      emit('change', getNumericValue());
    };

    const handleBlur = () => {
      emit('blur', getNumericValue());
    };

    const handleFocus = (event: FocusEvent) => {
      nextTick(() => {
        const target = event.target as HTMLInputElement | null;
        target?.select();
      });
      emit('focus', event);
    };

    return {
      formattedValue,
      inputRef,
      handleInput,
      handleChange,
      handleBlur,
      handleFocus,
    };
  },
});
